import { createBrowserRouter, createRoutesFromElements, Outlet, Route } from 'react-router';
import PageLayout from './PageLayout';
import ForgotPassword from '../screens/NotLoggedIn/ForgotPassword';
import ConfirmAccount from '../screens/NotLoggedIn/ConfirmAccount';
import ResetPassword from '../screens/NotLoggedIn/ResetPassword';
import SignIn from '../screens/NotLoggedIn/SignIn';
import Callback from '../screens/NotLoggedIn/Callback';
import PrivateRoute from './PrivateRoute';
import ProjectRoute from './ProjectRoute';
import Configuration from '../screens/Configuration/Configuration';
import Calculations from '../screens/Calculations/Calculations';
import CalculationDetail from '../screens/CalculationDetail/CalculationDetail';
import ProjectAdministration from '../screens/ProjectAdministration/ProjectAdministration';
import UserSettings from '../screens/UserSettings/UserSettings';
import Detail from '../screens/Detail/Detail';
import ConfigurationCategories from '../screens/Configuration/ConfigurationCategories/ConfigurationCategories';
import ConfigurationRegions from '../screens/Configuration/ConfigurationRegions/ConfigurationRegions';
import ConfigurationSkuClasses from '../screens/Configuration/ConfigurationSkuClasses/ConfigurationSkuClasses';
import ConfigurationProductLists from '../screens/Configuration/ConfigurationProductLists/ConfigurationProductLists';
import ConfigurationBrands from '../screens/Configuration/ConfigurationBrands/ConfigurationBrands';
import ConfigurationDrillDown from '../screens/Configuration/ConfigurationDrillDown/ConfigurationDrillDown';
import { RoutingPage } from './routerSlice';
import { ProjectAdministrationSubpage } from '../screens/ProjectAdministration/projectAdministrationLib';
import CalculationRedistributionSubpage from '../screens/CalculationDetail/Redistribution/CalculationRedistributionSubpage';
import CalculationDetailSubpage from '../screens/CalculationDetail/CalculationDetailSubpage';
import CalculationStatistics from '../screens/CalculationDetail/Statistics/CalculationStatistics';
import ConfigurationStoresAndDepartments from '../screens/Configuration/ConfigurationStoresAndDepartments/ConfigurationStoresAndDepartments';
import CalculationEvaluation from '../screens/CalculationDetail/Evaluation/CalculationEvaluation';
import ConfigurationBalancing from '../screens/Configuration/ConfigurationBalancing/ConfigurationBalancing';
import CalculationRequestListStatistics from '../screens/CalculationDetail/Statistics/CalculationRequestListStatistics';
import EntityListsAdministration from '../screens/ProjectAdministration/EntityListAdministration/EntityListsAdministration';
import { EntityListTypeName } from '@ydistri/api-sdk';
import CalculationPickingResults from '../screens/CalculationDetail/PickingResults/CalculationPickingResults';
import YPickCredentialsAdministration from '../screens/ProjectAdministration/YPickCredentials/YPickCredentialsAdministration';
import StoresScheduleAdministration from '../screens/ProjectAdministration/StoresSchedule/StoresScheduleAdministration';
import AiPromptsAdministration from '../screens/ProjectAdministration/AiPrompts/AiPromptsAdministration';
import { NotFound } from '@ydistri/ds';
import AsoScreen from '../screens/Aso/AsoScreen';
import Aso from '../screens/Aso/Aso';
import CustomExport from '../screens/CalculationDetail/CustomExport/CustomExport';
import ScopeAdministration from '../screens/ProjectAdministration/ScopeAdministration/ScopeAdministration';
import McpAuthorize from '../screens/McpAuthorize/McpAuthorize';

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<NotFound />}>
      <Route path="/account/forgot-pass/" element={<ForgotPassword />} />
      <Route path="/account/confirm/:token" element={<ConfirmAccount />} />
      <Route path="/account/reset/:token" element={<ResetPassword />} />
      <Route path="/callback" element={<Callback />} />
      <Route path="/login" element={<SignIn />} />
      {/* The MCP client's approval page: authenticated, but no project - a sibling of the project tree. */}
      <Route path="/mcp/authorize/:requestId" element={<PrivateRoute component={McpAuthorize} />} />
      <Route path="/" element={<PrivateRoute component={ProjectRoute} />}>
        <Route path="/:projectShortName" element={<PageLayout />} errorElement={<NotFound />}>
          <Route path="user-settings" element={<UserSettings />} />
          <Route path={RoutingPage.PROJECT_ADMINISTRATION} element={<ProjectAdministration />}>
            <Route
              path={`${ProjectAdministrationSubpage.MINLAYERLISTS}/:entityListId?`}
              element={<EntityListsAdministration entityListType={EntityListTypeName.MinLayer} />}
            />
            <Route
              path={`${ProjectAdministrationSubpage.PRODUCTLISTS}/:entityListId?`}
              element={<EntityListsAdministration entityListType={EntityListTypeName.Product} />}
            />
            <Route
              path={`${ProjectAdministrationSubpage.REQUESTLISTS}/:entityListId?`}
              element={<EntityListsAdministration entityListType={EntityListTypeName.Request} />}
            />
            <Route
              path={`${ProjectAdministrationSubpage.SOURCELISTS}/:entityListId?`}
              element={<EntityListsAdministration entityListType={EntityListTypeName.Source} />}
            />
            <Route
              path={`${ProjectAdministrationSubpage.TARGETLISTS}/:entityListId?`}
              element={<EntityListsAdministration entityListType={EntityListTypeName.Target} />}
            />
            <Route
              path={`${ProjectAdministrationSubpage.FORCEDLISTS}/:entityListId?`}
              element={<EntityListsAdministration entityListType={EntityListTypeName.Forced} />}
            />
            <Route
              path={ProjectAdministrationSubpage.YPICK_CREDENTIALS}
              element={<YPickCredentialsAdministration />}
            />
            <Route
              path={ProjectAdministrationSubpage.STORES_SCHEDULE}
              element={<StoresScheduleAdministration />}
            />
            <Route
              path={ProjectAdministrationSubpage.AI_PROMPTS}
              element={<AiPromptsAdministration />}
            />
            <Route
              path={`${ProjectAdministrationSubpage.SCOPE}/:scopeId?`}
              element={<ScopeAdministration />}
            />
          </Route>
          <Route path="detail/:slug?" element={<Detail />} />
          <Route path="calculation-detail" element={<CalculationDetail />}>
            <Route path=":calculationId" element={<Outlet />}>
              <Route path="redistribution/:slug?" element={<CalculationRedistributionSubpage />} />
              <Route path="statistics/:slug?" element={<CalculationStatistics />} />
              <Route
                path="request-list-statistics/:slug?"
                element={<CalculationRequestListStatistics />}
              />
              <Route path="categories/:slug?" element={<ConfigurationCategories />} />
              <Route path="regions/:slug?" element={<ConfigurationRegions />} />
              <Route path="stores/:slug?" element={<ConfigurationStoresAndDepartments />} />
              <Route path="skuclasses/:slug?" element={<ConfigurationSkuClasses />} />
              <Route path="productlists/:slug?" element={<ConfigurationProductLists />} />
              <Route path="brands/:slug?" element={<ConfigurationBrands />} />
              <Route path="advanced/:slug?" element={<ConfigurationBalancing />} />
              <Route path="drilldown/:slug?" element={<ConfigurationDrillDown />} />
              <Route path="evaluation/:slug?" element={<CalculationEvaluation />} />
              <Route path="custom-export/:slug?" element={<CustomExport />} />
              <Route
                path="picking-results/:pickingResultsPart/:slug?"
                element={<CalculationPickingResults />}
              />
              <Route path=":subpage" element={<CalculationDetailSubpage />}>
                <Route path=":slug" element={<Outlet />} />
              </Route>
            </Route>
          </Route>
          <Route path="aso" element={<AsoScreen />}>
            <Route index element={<Aso />} />
            <Route path=":asoRequestId" element={<Aso />} />
            <Route path=":asoRequestId/:asoScenarioId" element={<Aso />} />
          </Route>
          <Route path="configuration" element={<Configuration />}>
            <Route path=":templateId" element={<Outlet />}>
              <Route path="categories/:slug?" element={<ConfigurationCategories />} />
              <Route path="regions/:slug?" element={<ConfigurationRegions />} />
              <Route path="stores/:slug?" element={<ConfigurationStoresAndDepartments />} />
              <Route path="skuclasses/:slug?" element={<ConfigurationSkuClasses />} />
              <Route path="productlists/:slug?" element={<ConfigurationProductLists />} />
              <Route path="brands/:slug?" element={<ConfigurationBrands />} />
              <Route path="advanced/:slug?" element={<ConfigurationBalancing />} />
              <Route path="drilldown/:slug?" element={<ConfigurationDrillDown />} />
            </Route>
          </Route>
          <Route path="calculations" element={<Calculations />} />
          <Route path="" element={<Calculations />} />
        </Route>
      </Route>
    </Route>,
  ),
);
